import axios from 'axios'
import TransitApiConfig from '../../../routes/phase5/TransitOperationEditor/hooks/apiConfig'
import { removePrefix } from '../../../shared/ncts/common/classifier-util'

const { baseUrl } = TransitApiConfig.paths.transitOperation

/** The declaration fields the panel shows. */
export interface DeclarationRef {
  id: number
  declarationType: string
  departureOffice: string
  destinationOffice: string
  identifier: string
  identifierKind: 'MRN' | 'LRN' | ''
  status: string
}

interface RawDeclaration {
  id?: number
  declarationType?: string
  customsOfficeOfDeparture?: string
  customsOfficeOfDestination?: string
  lrn?: string
  mrn?: string
  status?: string
}

function normalize(raw: RawDeclaration): DeclarationRef {
  const identifier = raw.mrn || raw.lrn || ''
  let identifierKind: DeclarationRef['identifierKind'] = ''
  if (raw.mrn) identifierKind = 'MRN'
  else if (raw.lrn) identifierKind = 'LRN'
  return {
    id: raw.id ?? 0,
    declarationType: raw.declarationType ?? '',
    departureOffice: raw.customsOfficeOfDeparture ?? '',
    destinationOffice: raw.customsOfficeOfDestination ?? '',
    identifier,
    identifierKind,
    status: raw.status ?? '',
  }
}

export async function fetchDeclaration(id: number, signal?: AbortSignal): Promise<DeclarationRef> {
  // `id` is interpolated straight into the URL path below. `baseUrl` is a fixed constant so the
  // host/protocol can't be influenced, but a non-integer id (e.g. a malformed transitOperationId
  // relayed from the copilot service) would still land unchecked in the path segment — reject it
  // up front rather than let it reach axios. Callers (OutcomeBar) already treat a rejected fetch as
  // "no extra details available" via their existing .catch(), so this needs no new handling there.
  if (!Number.isInteger(id)) {
    throw new Error(`fetchDeclaration: id must be an integer, got ${String(id)}`)
  }
  const { data } = await axios.get<RawDeclaration>(`${baseUrl}/${id}`, { signal })
  return normalize(data)
}

/** `T1 · EE5500EE → EE1210EE · LRN 26EE…` — every part omitted when the field is absent. */
export function metaLine(ref: DeclarationRef): string {
  const parts: string[] = []
  if (ref.declarationType) parts.push(ref.declarationType)
  if (ref.departureOffice && ref.destinationOffice) {
    parts.push(`${(removePrefix(ref.departureOffice, 'DEPARTURE_OFFICE_'))} → ${removePrefix(ref.destinationOffice, 'DESTINATION_OFFICE_')}`)
  }
  if (ref.identifier) parts.push(`${ref.identifierKind} ${ref.identifier}`.trim())
  return parts.join(' · ')
}
